import { z } from 'zod';

export const AI_CAPABILITIES = [
  'source_analysis',
  'campaign_intelligence',
  'master_post_generation',
  'platform_adaptation',
  'social_output_generation',
  'social_output_regeneration',
  'safety_assisted_evaluation',
  'semantic_fingerprint',
  'external_signal_enrichment',
  'gateway_validation',
] as const;
export type AiCapability = (typeof AI_CAPABILITIES)[number];
export type AiServiceMode = 'spox_managed'|'customer_managed'|'disabled';
export type AiDataClassification = 'public_source'|'unpublished_editorial'|'publication_configuration'|'sensitive_internal';
export type AiOveragePolicy = 'deny_at_limit'|'allow_overage'|'admin_override';

export interface AiCapabilityDefinition {
  capability: AiCapability;
  costCategory: 'text'|'embedding';
  maxInputBytes: number;
  maxOutputBytes: number;
  timeoutMs: number;
  maxRetries: number;
  externalRequired: boolean;
  deterministicAvailable: boolean;
  allowedDataClasses: readonly AiDataClassification[];
}

const local = { maxInputBytes: 512_000, maxOutputBytes: 256_000, timeoutMs: 30_000, maxRetries: 1, externalRequired: false, deterministicAvailable: true, allowedDataClasses: ['public_source','unpublished_editorial','publication_configuration'] as const };
export const AI_CAPABILITY_REGISTRY = Object.fromEntries(AI_CAPABILITIES.map((capability)=>[capability,capability==='gateway_validation'
  ? {capability,costCategory:'text',maxInputBytes:2_048,maxOutputBytes:1_024,timeoutMs:15_000,maxRetries:0,externalRequired:true,deterministicAvailable:false,allowedDataClasses:['public_source'] as const}
  : {capability,costCategory:capability==='semantic_fingerprint'?'embedding':'text',...local}])) as unknown as Record<AiCapability,AiCapabilityDefinition>;

export interface AiUsageUnits { inputTokens?:number; outputTokens?:number; imageCount?:number; imagePixels?:number; audioSeconds?:number; videoSeconds?:number; embeddingTokens?:number; embeddingItems?:number; embeddingDimensions?:number; }
export interface AiProviderResult<T> { value:T; providerRequestId?:string; usage:AiUsageUnits; }
export interface AiProviderExecution<TInput=unknown> { capability:AiCapability; model:string; input:TInput; dataClassification:AiDataClassification; timeoutMs:number; idempotencyKey:string; }
export interface AiProviderAdapter { readonly id:string; readonly isExternal:boolean; readonly supportedCapabilities:readonly AiCapability[]; execute(request:AiProviderExecution<unknown>):Promise<AiProviderResult<unknown>>; }

export interface AiGatewayContext { publicationId:string; actorId?:string; sourceId?:string; campaignId?:string; socialOutputId?:string; }
export interface AiGatewayRequest<T> { capability:AiCapability; context:AiGatewayContext; input:T; idempotencyKey:string; dataClassification:AiDataClassification; estimatedCredits:number; intentionalRegeneration?:boolean; }
export interface AiGatewayResult<T> { value:T; requestId:string; provider:string; model:string; fallbackUsed:boolean; usage:AiUsageUnits; customerCredits:number; providerCostMicros:number; reused:boolean; }

export interface PortableIntelligenceEnvelope<T> { schemaVersion:'spox-intelligence-v1'; publicationId:string; capability:AiCapability; sourceVersionId?:string; generatedAt:string; gatewayRequestId:string; payload:T; }
export interface IntelligenceLearningExport { publicationId:string; generatedAt:string; records:readonly {type:string;entityId:string;provenance:Record<string,unknown>}[]; }

export const usageUnitsSchema=z.object({inputTokens:z.number().int().nonnegative().optional(),outputTokens:z.number().int().nonnegative().optional(),imageCount:z.number().int().nonnegative().optional(),imagePixels:z.number().int().nonnegative().optional(),audioSeconds:z.number().nonnegative().optional(),videoSeconds:z.number().nonnegative().optional(),embeddingTokens:z.number().int().nonnegative().optional(),embeddingItems:z.number().int().nonnegative().optional(),embeddingDimensions:z.number().int().nonnegative().optional()}).strict();
